import { useFlag } from '@duke-hq/flags/react';
import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  DrawerHeader,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import { calculateEnrolmentMetrics } from '@duke-hq/svc-api/lib/enrolment/calculateEnrolmentMetrics';
import {
  CourseSegment,
  isCourseSegment,
  SkillSetContractCategories,
} from '@duke-hq/shared-constants/enrolments';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { CirclePlus, Ellipsis, ScrollText, CalendarClock, X } from 'lucide-react';
import { skipToken } from '@tanstack/react-query';
import ActivityTracker from '@duke-hq/enrolments-ui/components/EnrolmentDetails/ActivityTracker';
import AssessmentsInRevision from '@duke-hq/enrolments-ui/components/EnrolmentDetails/AssessmentsInRevision';
import EnrolmentMetadata from '@duke-hq/enrolments-ui/components/EnrolmentDetails/EnrolmentMetadata';
import EnrolmentProgress from '@duke-hq/enrolments-ui/components/EnrolmentDetails/EnrolmentProgress';
import StudentDetails from '@duke-hq/enrolments-ui/components/EnrolmentDetails/StudentDetails';
import { onClickAddSemester } from '../Enrolments/enrolmentUtils';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { CourseKind } from '@duke-hq/svc-api-db';
import type { AssessmentInRevision } from '@duke-hq/svc-api/lib/assessment/types';
import EnrolmentDocumentsModal from '@duke-hq/enrolments-ui/components/EnrolmentDocumentsModal';
import { useEnrolmentDocumentQuery } from '../../lib/useEnrolmentDocumentQuery';
import EnrolmentBookCoachingModal from '@duke-hq/app-provider-web/components/EnrolmentBookCoachingModal';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { CourseSupersessionEventTypeSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseSupersessionEventType.schema';
import { iconSize } from '@duke-hq/ui/constants/scalable';

interface EnrolmentHeaderProps {
  enrolment: NonNullable<RouterOutputs['enrolments']['byIdWithResourceStatus']>;
  assessmentsInRevision?: AssessmentInRevision[];
  onClose?: () => void;
}

export const Header = React.memo(
  ({ enrolment, assessmentsInRevision, onClose }: EnrolmentHeaderProps): React.JSX.Element => {
    const { user, course, contract_category } = enrolment;
    const enrolmentMetrics = calculateEnrolmentMetrics(enrolment);
    const { currentSemester } = enrolmentMetrics;
    const [isEnrolmentDocument, setIsEnrolmentDocument] = useState<boolean>(false);
    const [isEnrolmentBookCoaching, setIsEnrolmentBookCoaching] = useState<boolean>(false);

    const isStudentTimesheetEnabled = useFlag('PROVIDER_STUDENT_TIMESHEET_RELEASE') === true;
    const getPdfDocumentDataFn = useEnrolmentDocumentQuery();

    const { data: learner } = trpcTenant.users.learnerByEmail.useQuery(
      user.email !== null && user.email !== '' ? { email: user.email } : skipToken,
      {
        enabled:
          user.email !== null &&
          user.email !== '' &&
          contract_category === String(SkillSetContractCategories.INTERNAL_ENROLMENTS),
      },
    );

    const { data: weeks } = trpcTenant.enrolments.getWeeklyActivityAndContacts.useQuery({
      courseEnrolmentId: enrolment.id,
      semester: currentSemester ?? 1,
    });

    // Query supersession history when course is superseded
    const { data: supersededCourse } = trpcTenant.courses.courseSupersessionHistory.useQuery(
      course?.status === CourseStatusSchema.enum.SUPERSEDED ? { courseId: course.id } : skipToken,
    );

    const isSkillSet = useMemo(
      () => enrolment.course?.kind === CourseKind.SKILL_SET,
      [enrolment.course?.kind],
    );

    return (
      <DrawerHeader
        backgroundColor="bg.subtle"
        w="full"
        p={4}
        borderBottomWidth={1}
        borderColor="border.default"
      >
        <HStack justify="space-between" align="start">
          <Text fontWeight="semibold" fontSize="2xl" mb={2}>
            {enrolment.contract_category ===
            String(SkillSetContractCategories.INTERNAL_ENROLMENTS) ? (
              <Trans>Internal Enrolment details</Trans>
            ) : (
              <Trans>Enrolment details</Trans>
            )}
          </Text>

          <HStack>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<CalendarClock size={iconSize.md} />}
              onClick={() => setIsEnrolmentBookCoaching(true)}
            >
              <Trans>Book a coaching call</Trans>
            </Button>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<Ellipsis size={iconSize.lg} />}
                variant="ghost"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              />
              <MenuList>
                {isCourseSegment(enrolment.course_segment) &&
                enrolment.course_segment === CourseSegment.FIRST_SEMESTER_ONLY ? (
                  <MenuItem
                    onClick={(e) => onClickAddSemester(enrolment, e)}
                    icon={<CirclePlus size={iconSize.md} />}
                  >
                    <Trans>Add semester</Trans>
                  </MenuItem>
                ) : null}
                <MenuItem
                  icon={<ScrollText size={iconSize.md} />}
                  onClick={() => setIsEnrolmentDocument(true)}
                >
                  <Trans>Documents</Trans>
                </MenuItem>
              </MenuList>
            </Menu>
            <IconButton
              aria-label="Close"
              icon={<X size={iconSize.lg} />}
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
          </HStack>
        </HStack>

        <EnrolmentMetadata
          course={course}
          enrolmentMetrics={enrolmentMetrics}
          enrolment={enrolment}
          isSkillSet={isSkillSet}
          labels={{
            accredited: t`Accredited`,
            consultant: t`Consultant:`,
            segment: t`Segment`,
            pace: t`Pace`,
            fullTime: t`Full-time`,
            partTime: t`Part-time`,
            startDate: t`Start date`,
            firstActivity: t`First activity`,
            targetDate: t`Target date`,
            contract: t`Contract`,
          }}
        />

        {supersededCourse != null &&
        supersededCourse.supersededByCourse != null &&
        supersededCourse.eventType ===
          CourseSupersessionEventTypeSchema.enum.SUPERSEDED_TOGGLED_ON ? (
          <VStack w="full" gap="8px" alignItems="right" justifyContent="center" mt={3}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon alignSelf="flex-start" mt="2px" />
              <AlertDescription>
                <VStack align="start" spacing="0px" gap="0px">
                  <Text fontSize="sm" lineHeight="1.5">
                    <Trans>
                      This course has been superseded. Enrolment in Second Semester will require a
                      transfer to the new
                    </Trans>
                    {supersededCourse.supersededByCourse?.identifier != null
                      ? ` ${supersededCourse.supersededByCourse.identifier}`
                      : null}
                    <Trans> course</Trans>
                  </Text>
                </VStack>
              </AlertDescription>
            </Alert>
          </VStack>
        ) : null}

        <Flex backgroundColor="bg.subtle" w="full" gap={3} mt={3}>
          <StudentDetails
            user={user}
            learner={learner}
            labels={{
              learner: t`Learner`,
              student: t`Student`,
              viewProfile: t`View profile`,
            }}
          />
          <EnrolmentProgress
            enrolment={enrolment}
            enrolmentMetrics={enrolmentMetrics}
            isSkillSet={isSkillSet}
            labels={{
              progress: t`Progress`,
              semester: t`Semester`,
              placement: t`Placement`,
            }}
          />
        </Flex>

        {/* Weekly Activity Tracking and Assessment Revision */}
        <Flex backgroundColor="bg.subtle" w="full" gap={3} mt={3}>
          <ActivityTracker
            weeks={weeks}
            currentSemester={
              enrolment.course?.kind === CourseKind.QUALIFICATION ? currentSemester : null
            }
            labels={{
              noStudentDetails: t`No student details.`,
              unableToContact: t`Unable to contact.`,
              activity: t`Activity`,
              contacts: t`Contacts`,
            }}
          />
          {assessmentsInRevision !== undefined ? (
            <AssessmentsInRevision
              assessmentsInRevision={assessmentsInRevision}
              labels={{ title: t`Assessments in revision` }}
            />
          ) : null}
        </Flex>

        <EnrolmentDocumentsModal
          enrolment={enrolment}
          isOpen={isEnrolmentDocument}
          onClose={() => setIsEnrolmentDocument(false)}
          getPdfDocumentDataFn={getPdfDocumentDataFn}
          isAdmin={false}
          isStudentTimesheetEnabled={isStudentTimesheetEnabled}
          labels={{
            title: t`Enrolment Documents`,
            emptyMessage: t`This enrolment doesn't have any documents available at the moment.`,
            view: t`View`,
            done: t`Done`,
          }}
        />

        <EnrolmentBookCoachingModal
          student={{
            id: user.id,
            firstName: user.first_name ?? '',
            lastName: user.last_name ?? '',
            email: user.email ?? '',
            picture_url: user.picture_url ?? undefined,
            universal_student_identifier:
              user.studentData?.universal_student_identifier ?? undefined,
          }}
          enrolment={{
            id: enrolment.id,
            course: {
              id: enrolment?.course?.id ?? '',
              identifier: enrolment?.course?.identifier ?? '',
              title: enrolment?.course?.title ?? '',
              coachingCallLink: enrolment?.course?.coachingCallLink ?? null,
            },
          }}
          isOpen={isEnrolmentBookCoaching}
          onClose={() => setIsEnrolmentBookCoaching(false)}
        />
      </DrawerHeader>
    );
  },
);

Header.displayName = 'EnrolmentDetailsHeader';
